import {
  getSnakeBodySegmentCount as calculateSnakeBodySegmentCount,
  type PlayerBodyConfig,
} from "../config/gameplay-config";
import type { Vector2 } from "./player-movement";

export interface SnakeBodySource {
  readonly directionRadians: number;
  readonly level: number;
  readonly position: Vector2;
  readonly size: number;
}

export interface PlayerBodySegment {
  readonly position: Vector2;
  readonly radius: number;
}

export interface PlayerBodyTrailState {
  readonly path: readonly Vector2[];
  readonly rippleStartedAtMs: number | null;
  readonly segments: readonly PlayerBodySegment[];
}

export type SnakeBodySegment = PlayerBodySegment;
export type SnakeBodyTrailState = PlayerBodyTrailState;

const distanceBetween = (first: Vector2, second: Vector2): number =>
  Math.hypot(second.x - first.x, second.y - first.y);

const pointBetween = (
  first: Vector2,
  second: Vector2,
  ratio: number,
): Vector2 => ({
  x: first.x + (second.x - first.x) * ratio,
  y: first.y + (second.y - first.y) * ratio,
});

export const getPlayerBodySegmentCount = calculateSnakeBodySegmentCount;

export const getSnakeBodySegmentCount = calculateSnakeBodySegmentCount;

const seedPath = (
  player: SnakeBodySource,
  config: PlayerBodyConfig,
): readonly Vector2[] => {
  const pointCount =
    Math.ceil(config.historyMaximumDistance / config.interpolationDistance) + 1;
  const backwardX = Math.cos(player.directionRadians);
  const backwardY = Math.sin(player.directionRadians);
  return Array.from({ length: pointCount }, (_, index) => ({
    x: player.position.x - backwardX * config.interpolationDistance * index,
    y: player.position.y - backwardY * config.interpolationDistance * index,
  }));
};

const appendPath = (
  path: readonly Vector2[],
  position: Vector2,
  maximumDistance: number,
): readonly Vector2[] => {
  const head = path[0];
  if (head !== undefined && distanceBetween(head, position) === 0) {
    return path;
  }
  const nextPath: Vector2[] = [{ ...position }];
  let traveled = 0;
  let previous = position;
  for (const point of path) {
    const distance = distanceBetween(previous, point);
    if (traveled + distance > maximumDistance) {
      nextPath.push(
        pointBetween(previous, point, (maximumDistance - traveled) / distance),
      );
      break;
    }
    nextPath.push({ ...point });
    traveled += distance;
    previous = point;
    if (traveled === maximumDistance) {
      break;
    }
  }
  return nextPath;
};

const samplePathDistances = (
  path: readonly Vector2[],
  distancesFromHead: readonly number[],
): readonly Vector2[] => {
  const fallback = path.reduce((head) => head);
  const samples: Vector2[] = [];
  let traveled = 0;
  let previous = fallback;
  let pathIndex = 1;
  for (const distanceFromHead of distancesFromHead) {
    while (pathIndex < path.length) {
      const point = path[pathIndex];
      if (point === undefined) {
        break;
      }
      const distance = distanceBetween(previous, point);
      if (traveled + distance >= distanceFromHead) {
        samples.push(
          pointBetween(
            previous,
            point,
            (distanceFromHead - traveled) / distance,
          ),
        );
        break;
      }
      traveled += distance;
      previous = point;
      pathIndex += 1;
    }
    if (pathIndex >= path.length) {
      samples.push({ ...previous });
    }
  }
  return samples;
};

const buildSegments = (
  player: SnakeBodySource,
  path: readonly Vector2[],
  config: PlayerBodyConfig,
): readonly PlayerBodySegment[] => {
  const count = getPlayerBodySegmentCount(player.level, config);
  const spacing = player.size * config.segmentSpacingScale;
  const positions = samplePathDistances(
    path,
    Array.from({ length: count }, (_, index) => spacing * (index + 1)),
  );
  return positions.map((position, index) => {
    const taperProgress = count === 1 ? 1 : index / (count - 1);
    const radiusScale =
      config.bodyRadiusScale +
      (config.tailRadiusScale - config.bodyRadiusScale) * taperProgress;
    return {
      position,
      radius: Math.max(player.size * radiusScale, config.minimumTailRadius),
    };
  });
};

export const createPlayerBodyTrail = (
  player: SnakeBodySource,
  config: PlayerBodyConfig,
): PlayerBodyTrailState => {
  const path = seedPath(player, config);
  return {
    path,
    rippleStartedAtMs: null,
    segments: buildSegments(player, path, config),
  };
};

export const createSnakeBodyTrail = createPlayerBodyTrail;

export const advancePlayerBodyTrail = (
  state: PlayerBodyTrailState,
  player: SnakeBodySource,
  config: PlayerBodyConfig,
): PlayerBodyTrailState => {
  const path = appendPath(
    state.path,
    player.position,
    config.historyMaximumDistance,
  );
  return {
    path,
    rippleStartedAtMs: state.rippleStartedAtMs,
    segments: buildSegments(player, path, config),
  };
};

export const advanceSnakeBodyTrail = (
  state: SnakeBodyTrailState,
  snake: SnakeBodySource,
  config: PlayerBodyConfig,
): SnakeBodyTrailState => {
  const head = state.path[0];
  return head !== undefined &&
    distanceBetween(head, snake.position) < config.interpolationDistance
    ? state
    : advancePlayerBodyTrail(state, snake, config);
};

export const advanceSnakeBodyTrailPath = (
  state: SnakeBodyTrailState,
  snake: SnakeBodySource,
  config: PlayerBodyConfig,
): SnakeBodyTrailState => {
  const head = state.path[0];
  if (
    head !== undefined &&
    distanceBetween(head, snake.position) < config.interpolationDistance
  ) {
    return state;
  }
  return {
    ...state,
    path: appendPath(state.path, snake.position, config.historyMaximumDistance),
  };
};

export const materializeSnakeBodyTrail = (
  state: SnakeBodyTrailState,
  snake: SnakeBodySource,
  config: PlayerBodyConfig,
): SnakeBodyTrailState => ({
  ...state,
  segments: buildSegments(snake, state.path, config),
});

export const triggerPlayerBodyRipple = (
  state: PlayerBodyTrailState,
  simulationTimeMs: number,
): PlayerBodyTrailState => ({
  ...state,
  rippleStartedAtMs: simulationTimeMs,
});

export const getPlayerBodyRippleScale = (
  state: PlayerBodyTrailState,
  segmentIndex: number,
  simulationTimeMs: number,
  motionScale: number,
  config: PlayerBodyConfig,
): number => {
  const startedAt = state.rippleStartedAtMs;
  if (startedAt === null || motionScale === 0) {
    return 1;
  }
  const overallProgress =
    (simulationTimeMs - startedAt) / config.rippleDurationMs;
  if (overallProgress < 0 || overallProgress >= 1) {
    return 1;
  }
  const segmentProgress = segmentIndex / state.segments.length;
  const waveStart = segmentProgress * (1 - config.rippleWaveLength);
  const localProgress = (overallProgress - waveStart) / config.rippleWaveLength;
  if (localProgress <= 0 || localProgress >= 1) {
    return 1;
  }
  return (
    1 + Math.sin(localProgress * Math.PI) * config.rippleAmplitude * motionScale
  );
};
